import React, { useCallback, useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import { Ledger, StatusCell, What, Who } from '../shared/cells';
import { fetch_payout_eligibility, fetch_payout_file_csv, fetch_payout_files } from '../api';
import {
  bank_file_name,
  eligibility_view,
  files_view,
  generate_choices,
  generate_month_options,
  over_limit_text,
  payout_error
} from './adapters';
import GenerateDialog from './generate_dialog';
import ListCard from './list_card';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { ELIGIBLE_COLUMNS, FILE_COLUMNS, LEFT_OUT_COLUMNS, payouts_csv_name } from './csv';

const LOAD_FAILED_TEXT = 'Could not load this month’s bank file details.';
const DOWNLOAD_FAILED_TEXT = 'Could not download the bank file. Try again.';

// The teacher's name opens the teacher-month pop-up.
const teacher_name_link = (on_open, r) => (
  <button
    type="button"
    className="tp-link tp-po-name"
    aria-label={`Open ${r.teacher_name}'s payouts`}
    onClick={() => on_open(r.teacher_uuid)}
  >
    {r.teacher_name}
  </button>
);

const check_pills = (r) =>
  // Nothing to check (no flags, not a retry, nothing withheld): no pills, so a card has no empty block.
  !r.flags.length && !r.retry && !r.withheld ? null : (
    <div className="tp-po-pills">
      {r.flags.map((f, i) => (
        // eslint-disable-next-line react/no-array-index-key -- codes may repeat; the list is static
        <span key={`${f.code}-${i}`} title={f.text || undefined}>
          <StatusPill tone={f.tone}>{f.label}</StatusPill>
        </span>
      ))}
      {r.retry ? <StatusPill tone="blue">Paying again after a failed line</StatusPill> : null}
      {r.withheld ? (
        <StatusPill tone="orange">{`${r.withheld} withheld: duplicate payment recovery`}</StatusPill>
      ) : null}
    </div>
  );

// Four tracks (list-row pattern): teacher · account · amount · checks; cards below 660px.
const eligible_columns = (on_open) => [
  {
    key: 'teacher',
    label: 'Teacher',
    role: 'identifier',
    track: { compact: '30%', full: '28%' },
    render: (r) => <Who name={teacher_name_link(on_open, r)} />
  },
  {
    key: 'account',
    label: 'Account',
    render: (r) => (
      <What
        title={<span className="tp-mono tp-po-acct">{r.account_number_masked}</span>}
        lines={[`${r.bank_name} · ${r.mode}`, `Branch: ${r.branch}`]}
      />
    )
  },
  {
    key: 'amount',
    role: 'amount',
    label: 'Amount',
    align: 'right',
    track: { compact: '140px', full: '148px' },
    render: (r) => <span className="tp-c-amt">{r.amount}</span>,
    card_render: (r) => <Ledger items={[{ label: 'Amount', value: r.amount }]} />
  },
  {
    key: 'flags',
    role: 'status',
    label: 'Check',
    track: { compact: '172px', full: '220px' },
    render: (r) => check_pills(r)
  }
];

const left_out_columns = (on_open) => [
  {
    key: 'teacher',
    label: 'Teacher',
    role: 'identifier',
    track: { compact: '30%', full: '28%' },
    render: (r) => <Who name={teacher_name_link(on_open, r)} />
  },
  {
    key: 'reason_text',
    role: 'status',
    label: 'Why not in the file',
    render: (r) =>
      r.flagged ? (
        <What
          title={<StatusPill tone={r.reason_view.tone}>{r.reason_view.label}</StatusPill>}
          lines={[r.reason_text]}
        />
      ) : (
        <span>{r.reason_text}</span>
      )
  },
  {
    key: 'outstanding',
    role: 'amount',
    label: 'Still owed',
    align: 'right',
    track: { compact: '140px', full: '148px' },
    render: (r) => <span className="tp-c-amt">{r.outstanding}</span>,
    card_render: (r) => <Ledger items={[{ label: 'Still owed', value: r.outstanding }]} />
  }
];

const file_columns = (on_download, busy_uuid) => {
  const download_button = (f, className) => {
    const busy = busy_uuid === f.uuid;
    return (
      <button
        type="button"
        className={className}
        aria-label={`Download bank file R${f.round_no}`}
        onClick={busy ? undefined : () => on_download(f)}
        aria-disabled={busy ? 'true' : undefined}
      >
        {busy ? 'Downloading…' : 'Download'}
      </button>
    );
  };
  const chip = (f) => <StatusPill tone={f.status_view.tone}>{f.status_view.label}</StatusPill>;
  const meta = (f) => [f.cancel_reason || null];
  return [
    {
      key: 'file_name',
      role: 'identifier',
      label: 'File',
      track: { compact: '38%', full: '36%' },
      render: (f) => (
        <Who
          name={`Round R${f.round_no}`}
          meta={[
            <span className="tp-mono tp-po-file-name">{f.file_name}</span>,
            `Made by ${f.generated_by} at ${f.generated_at}`,
            `Payment date ${f.payment_date}`
          ]}
        />
      )
    },
    {
      key: 'rows',
      label: 'Lines',
      render: (f) => (
        <What
          title={`${f.row_count} ${f.row_count === 1 ? 'line' : 'lines'}`}
          lines={[
            f.counts_text || 'None in a bank file',
            f.downloads.length
              ? `Downloaded: ${f.downloads.map((d) => `${d.by} at ${d.at}`).join('; ')}`
              : 'Never downloaded'
          ]}
        />
      )
    },
    {
      key: 'total',
      role: 'amount',
      label: 'Total',
      align: 'right',
      track: { compact: '140px', full: '148px' },
      render: (f) => <span className="tp-c-amt">{f.total}</span>,
      card_render: (f) => <Ledger items={[{ label: 'Total', value: f.total }]} />
    },
    {
      key: 'status',
      role: 'status',
      label: 'Status',
      track: { compact: '132px', full: '160px' },
      render: (f) => (
        <StatusCell
          chip={chip(f)}
          meta={meta(f)}
          actions={<span className="tp-c-acts tp-actions">{download_button(f, 'tp-link')}</span>}
        />
      ),
      card_render: (f) => <StatusCell chip={chip(f)} meta={meta(f)} />
    },
    {
      key: 'download',
      role: 'actions',
      label: 'Download',
      in_table: false,
      card_render: (f) => download_button(f, 'tp-btn-out')
    }
  ];
};

function FilePanel({ pay_month, month_label, months, on_open_teacher, on_notify, on_error }) {
  const [state, setState] = useState({ status: 'loading', elig: null, files: [], error: null });
  const [choice, setChoice] = useState(null);
  const [busy_uuid, setBusyUuid] = useState('');
  const [stale, setStale] = useState('');
  const seq = useRef(0);

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setState((s) => ({ ...s, status: 'loading' }));
    Promise.all([fetch_payout_eligibility(pay_month), fetch_payout_files(pay_month)])
      .then(([e, f]) => {
        if (mine !== seq.current) return;
        setState({ status: 'ready', elig: eligibility_view(e), files: files_view(f), error: null });
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = payout_error(e, LOAD_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, LOAD_FAILED_TEXT);
        setState({ status: 'error', elig: null, files: [], error: v });
      });
  }, [pay_month, on_error]);

  useEffect(() => {
    load();
  }, [load]);

  const download = (file) => {
    setBusyUuid(file.uuid);
    fetch_payout_file_csv(file.uuid)
      .then(({ blob, file_name }) => {
        setBusyUuid('');
        saveAs(blob, bank_file_name(file, file_name));
        load(); // the download log is part of what Ops check before a regenerate
      })
      .catch((e) => {
        setBusyUuid('');
        const v = payout_error(e, DOWNLOAD_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, DOWNLOAD_FAILED_TEXT);
        else on_notify('error', v.text);
      });
  };

  if (state.status === 'loading') return <CustomLoading />;
  if (state.status === 'error') {
    const k = state.error.kind;
    return k === 'forbidden' || k === 'before_cutover' ? (
      <div className="tp-banner tp-banner-info">{state.error.text}</div>
    ) : (
      <LoadFailed text={state.error.text} on_retry={load} />
    );
  }

  const { elig, files } = state;
  const choices = generate_choices(elig, files);
  const none_eligible = elig.eligible.length === 0;

  return (
    <div className="tp-po-file">
      {stale ? (
        <div className="tp-banner tp-banner-warn" role="alert">
          {stale}{' '}
          <button
            type="button"
            className="tp-link"
            onClick={() => {
              setStale('');
              load();
            }}
          >
            Reload
          </button>
        </div>
      ) : null}

      <div className="tp-stats tp-po-stats">
        <div className="tp-stat tp-stat-lead">
          <span className="tp-stat-label">Next bank file</span>
          <span className="tp-stat-value tp-nowrap">{elig.eligible_total}</span>
          <span className="tp-stat-chip">
            {elig.eligible.length} {elig.eligible.length === 1 ? 'teacher' : 'teachers'}
          </span>
        </div>
        <div className="tp-stat">
          <span className="tp-stat-label">Left out</span>
          <span className="tp-stat-value">{elig.left_out.length}</span>
          <span className="tp-stat-chip">Not in the next file</span>
        </div>
        <div className="tp-stat">
          <span className="tp-stat-label">Bank files</span>
          <span className="tp-stat-value">{files.length}</span>
          <span className="tp-stat-chip">For this month</span>
        </div>
      </div>
      {elig.large_count ? (
        <div className="tp-banner tp-banner-warn">
          {elig.large_count} {elig.large_count === 1 ? 'line is' : 'lines are'} a large amount:
          check before generating.
        </div>
      ) : null}
      {elig.over_limit_count ? (
        <div className="tp-banner tp-banner-warn" role="status">
          {over_limit_text(elig.over_limit_count)}
        </div>
      ) : null}
      {elig.above_average_count ? (
        <div className="tp-banner tp-banner-warn">
          {elig.above_average_count} {elig.above_average_count === 1 ? 'line is' : 'lines are'} more
          than twice the teacher’s 3-month average: check before generating.
        </div>
      ) : null}
      <ListCard
        title={month_label ? `Next file · ${month_label}` : 'Next file'}
        sub={
          <span className="tp-nowrap">
            {`${elig.eligible.length} ${elig.eligible.length === 1 ? 'teacher' : 'teachers'} · ${elig.eligible_total}`}
          </span>
        }
        acts={[
          <DownloadCsvButton
            key="csv"
            columns={ELIGIBLE_COLUMNS}
            rows={elig.eligible}
            file_name={payouts_csv_name('Next file', pay_month)}
          />,
          ...choices.map((c) => (
            <button
              key={c.key}
              type="button"
              className={c.kind === 'REGENERATE' ? 'tp-btn-out' : 'tp-btn'}
              title={
                c.disabled_text ||
                (none_eligible ? 'No teacher can be paid in this file.' : undefined)
              }
              aria-disabled={c.disabled_text || none_eligible ? 'true' : undefined}
              onClick={c.disabled_text || none_eligible ? undefined : () => setChoice(c)}
            >
              {c.label}
            </button>
          ))
        ]}
        note={
          none_eligible
            ? 'No teacher can be paid in a file right now: see who is left out below.'
            : ''
        }
      >
        <DataTable
          columns={eligible_columns(on_open_teacher)}
          rows={elig.eligible}
          rowKey={(r) => r.teacher_uuid}
          emptyText="Nobody to pay in the next file."
          card_below={660}
        />
      </ListCard>

      <ListCard
        title="Left out"
        sub={`${elig.left_out.length}`}
        acts={
          <DownloadCsvButton
            columns={LEFT_OUT_COLUMNS}
            rows={elig.left_out}
            file_name={payouts_csv_name('Left out', pay_month)}
          />
        }
      >
        <DataTable
          columns={left_out_columns(on_open_teacher)}
          rows={elig.left_out}
          rowKey={(r) => r.teacher_uuid}
          rowClassName={(r) => (r.flagged ? 'tp-row-hl' : '')}
          emptyText="Nobody is left out."
          card_below={660}
        />
      </ListCard>

      <ListCard
        title={month_label ? `Bank files · ${month_label}` : 'Bank files for this month'}
        sub={files.length ? `${files.length}` : ''}
        acts={
          <DownloadCsvButton
            columns={FILE_COLUMNS}
            rows={files}
            file_name={payouts_csv_name('Bank files', pay_month)}
          />
        }
      >
        <DataTable
          columns={file_columns(download, busy_uuid)}
          rows={files}
          rowKey={(f) => f.uuid}
          emptyText="No bank file for this month yet."
          card_below={660}
        />
      </ListCard>

      {choice ? (
        <GenerateDialog
          pay_month={pay_month}
          month_options={generate_month_options(months, elig.cutover_month, pay_month)}
          choice={choice}
          eligible_count={elig.eligible.length}
          eligible_total={elig.eligible_total}
          on_close={() => setChoice(null)}
          on_done={(text) => {
            setChoice(null);
            on_notify('success', text);
            load();
          }}
          on_stale={(text) => {
            setChoice(null);
            setStale(text);
          }}
          on_error={on_error}
        />
      ) : null}
    </div>
  );
}

export default FilePanel;
